/**
 * Scans agent/tool output for the [BILLING_PAYWALL:CODE|action] marker that
 * our MCP tools throw when a user hits a billing gate. Extracts the code +
 * action and fires the upgrade modal.
 *
 * The marker is emitted by:
 *   - packages/ivy-mcp-server/src/auth/billingGate.ts (direct MCP-side checks)
 *   - packages/ivy-mcp-server/src/apiClient.ts (api-server 402 propagation)
 *
 * We dedupe by marker-content so rapid re-streams of the same error don't
 * repeatedly open/close the modal.
 */

import { useUpgradeModal } from '../stores/upgradeModalStore';
import type { UIMessage } from '../lib/ivyChat/messageParser';

const MARKER_RE = /\[BILLING_PAYWALL:([A-Z_]+)(?:\|([^\]]+))?\]/;
const MARKER_RE_ALL = new RegExp(MARKER_RE.source, 'g');

let lastFiredMarker: string | null = null;
let lastFiredAt = 0;
const DEDUPE_WINDOW_MS = 5_000;

export function detectAndFireBillingPaywall(text: string | undefined | null): void {
  if (!text || typeof text !== 'string') return;
  const m = text.match(MARKER_RE);
  if (!m) return;

  const code = m[1];
  const action = m[2]?.trim() || undefined;
  const fullMarker = m[0];

  // Dedupe — same marker within 5s → already handled
  const now = Date.now();
  if (fullMarker === lastFiredMarker && now - lastFiredAt < DEDUPE_WINDOW_MS) {
    return;
  }
  lastFiredMarker = fullMarker;
  lastFiredAt = now;

  useUpgradeModal.getState().open({
    code,
    action,
    message: text.replace(MARKER_RE, '').trim().slice(0, 300) || undefined,
  });
}

export interface PaywallInfo {
  code?: string;
  action?: string;
  message?: string;
  needMilli?: number;
  availableMilli?: number;
}

/** An in-thread "out of credits" card: a system message the chat runtimes render as OutOfCreditsCard. */
export type OutOfCreditsMessage = UIMessage & { type: 'system'; subtype: 'out_of_credits'; paywall: PaywallInfo };

export const OUT_OF_CREDITS_PART = '[OUT_OF_CREDITS]\n';

export function isOutOfCreditsMessage(msg: UIMessage): msg is OutOfCreditsMessage {
  return msg.type === 'system' && msg.subtype === 'out_of_credits';
}

export function outOfCreditsMessage(paywall: PaywallInfo): OutOfCreditsMessage {
  return {
    id: `msg-out-of-credits-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'system',
    subtype: 'out_of_credits',
    content: paywall.message ?? '',
    timestamp: Date.now(),
    paywall,
  };
}

/** The text part the chat runtimes hand the thread; the thread swaps it for the card. */
export function outOfCreditsPart(msg: OutOfCreditsMessage): string {
  return OUT_OF_CREDITS_PART + JSON.stringify(msg.paywall);
}

export function parseOutOfCreditsPart(text: string): PaywallInfo | null {
  if (!text.startsWith(OUT_OF_CREDITS_PART)) return null;
  try {
    return JSON.parse(text.slice(OUT_OF_CREDITS_PART.length)) as PaywallInfo;
  } catch {
    return {};
  }
}

/**
 * Strips `[BILLING_PAYWALL:…]` markers from incoming tool results and assistant text, and appends
 * one out-of-credits card per turn in their place. `prev` is what the thread already holds.
 */
export function withPaywallCards(prev: UIMessage[], incoming: UIMessage[]): UIMessage[] {
  let carded = false;
  for (let i = prev.length - 1; i >= 0; i--) {
    if (isOutOfCreditsMessage(prev[i])) { carded = true; break; }
    if (prev[i].role === 'user') break;
  }
  const out: UIMessage[] = [];
  for (const msg of incoming) {
    if (msg.role === 'user') carded = false;
    const m = (msg.type === 'tool_result' || (msg.type === 'chat' && msg.role === 'assistant')) ? msg.content.match(MARKER_RE) : null;
    if (!m) {
      out.push(msg);
      continue;
    }
    const content = msg.content.replace(MARKER_RE_ALL, '').trim();
    if (msg.type === 'tool_result' || content) out.push({ ...msg, content });
    if (!carded) {
      out.push(outOfCreditsMessage({ code: m[1], action: m[2]?.trim() || undefined, message: content.slice(0, 300) || undefined }));
      carded = true;
    }
  }
  return out;
}

/**
 * A chat turn refused with 402 (no credits for the run): the paywall it carries, for the thread to
 * render in place. A refusal that is not about credits (BYOK_REQUIRED) opens the modal and gives null.
 */
export async function readPaywall402(res: Response): Promise<PaywallInfo | null> {
  const body = await res.json().catch(() => ({})) as { code?: string; error?: string; action?: string; needMilli?: number; availableMilli?: number };
  if (body.code === 'BYOK_REQUIRED') {
    useUpgradeModal.getState().open({ code: body.code, message: body.error });
    return null;
  }
  return { code: body.code, action: body.action, message: body.error, needMilli: body.needMilli, availableMilli: body.availableMilli };
}
